import { useMemo } from 'react';

import { useAntibodyTableData } from '../../antibody-analysis/hooks/useAntibodyTableData';
import type { AntibodyResponse, ClonotypeDefinitionEnum } from '../../generated/generatedOpenAPI';
import type { SelectedNode } from '../../store/slices/clonotypes.slice';
import { useListExports } from '../../store/store';
import type { ExportNode, ExportResponse, ExportRow } from '../types';

export interface UseExportSummaryDataResult {
  /** The export tabs, for the tab strip. */
  exports: ExportResponse[];
  /** The antibody data behind the rows. ChainWidthProvider measures chain widths from this. */
  clonotypes: AntibodyResponse[];
  /** The experiment's clonotyping method — needed for the CSV's clon_method column. */
  clonotypeDefinition: ClonotypeDefinitionEnum;
  /** Flat rows keyed by node id — every node across every export, joined with its antibody data. */
  rowsByNodeId: Map<number, ExportRow>;
  isLoading: boolean;
  error: unknown;
}

const NO_NODES: SelectedNode[] = [];
const NO_BARCODES: string[] = [];
const EMPTY_EXPORTS: ExportResponse[] = [];

/** "IGHV1-19/IGHJ3" — matches the Hit selection column formatting. */
function joinGenes(vGene: string | null | undefined, jGene: string | null | undefined): string {
  if (!vGene && !jGene) {
    return 'N/A';
  }
  return `${vGene ?? '—'}/${jGene ?? '—'}`;
}

/**
 * Everything the Export summary page needs, in one hook.
 *
 * Two calls, because the data is split across two endpoints by design:
 *
 *   1. the export API says *which* nodes are in each tab, and carries the export overlay
 *      (`exportNums`, `labels`) plus the keys needed for step 2;
 *   2. the antibody-table API supplies the *display columns* — V-J genes, CDRs, APS,
 *      liabilities, SHMs and the chain sequences the shared cell renderers need.
 *
 */
export function useExportSummaryData(experimentId: string | undefined): UseExportSummaryDataResult {
  const {
    data: exports = EMPTY_EXPORTS,
    isLoading: exportsLoading,
    error: exportsError,
  } = useListExports({ experimentUuid: experimentId ?? '' }, { skip: !experimentId });

  // De-duplicate across tabs: the same node can appear in several exports, but we only want
  // to fetch its antibody data once.
  const uniqueNodes = useMemo(() => {
    const byNodeId = new Map<number, ExportNode>();
    for (const exportTab of exports) {
      for (const node of exportTab.nodes) {
        if (!byNodeId.has(node.nodeId)) {
          byNodeId.set(node.nodeId, node);
        }
      }
    }
    return [...byNodeId.values()];
  }, [exports]);

  const clonotypeDefinition = (uniqueNodes[0]?.clonotypeDefinition ?? 'hilary') as ClonotypeDefinitionEnum;

  const nodes: SelectedNode[] = useMemo(
    () => uniqueNodes.map((node) => ({ clonotypeId: node.clonotypeId, nodeId: node.nodeId, cellIds: node.cellIds })),
    [uniqueNodes],
  );

  // useAntibodyTableData keys its cache on barcodes and skips the fetch entirely when the list
  // is empty, so these have to be the real barcodes rather than a placeholder.
  const barcodes: string[] = useMemo(() => uniqueNodes.flatMap((node) => node.originalBarcodes), [uniqueNodes]);

  const {
    clonotypes,
    isLoading: antibodyLoading,
    error: antibodyError,
  } = useAntibodyTableData({
    experimentId: experimentId ?? '',
    barcodes: barcodes.length === 0 ? NO_BARCODES : barcodes,
    nodes: nodes.length === 0 ? NO_NODES : nodes,
    clonotypeDefinition,
  });

  const rowsByNodeId = useMemo(() => {
    const rows = new Map<number, ExportRow>();

    // Join on barcode, because the antibody-table response carries no node id at all:
    // `metadata.sequenceId` is `IS{cloneId}-{ordinal}` (a position within the clonotype, see
    // multiple_alignment.py), so barcodes are the only value present on both sides.
    //
    // Both sides use `originalBarcodes` — the DB `cell.barcode` column (`AAAGCTAAGGTCATCA-1`).
    // `metadata.barcodes` is the short `barcode_id` display form (`BC4`) and never overlaps.
    const overlayByBarcode = new Map<string, ExportNode>();
    for (const node of uniqueNodes) {
      node.originalBarcodes.forEach((barcode) => overlayByBarcode.set(barcode, node));
    }

    for (const clonotype of clonotypes) {
      for (const sequence of clonotype.sequences) {
        const overlay = sequence.metadata.originalBarcodes.map((barcode) => overlayByBarcode.get(barcode)).find(Boolean);
        if (!overlay) {
          continue;
        }
        const { barcodes: seqBarcodes } = sequence.metadata;
        if (rows.has(overlay.nodeId)) {
          // Two antibody sequences resolved to one export node, so one of them would be lost.
          // Surfacing it beats a silently short table; see the node/sequence grouping question.
          // eslint-disable-next-line no-console
          console.warn(`Export summary: node ${overlay.nodeId} matched more than one sequence; keeping the first.`);
          continue;
        }
        rows.set(overlay.nodeId, {
          nodeId: overlay.nodeId,
          clonotypeId: overlay.clonotypeId,
          // Same derivation as InnerChildSequenceTable so the Node column reads identically.
          nodeLabel: seqBarcodes.length > 1 ? `${seqBarcodes[0]} (+${seqBarcodes.length - 1} more)` : (seqBarcodes[0] ?? sequence.metadata.sequenceId),
          exportNums: overlay.exportNums,
          labels: overlay.labels,
          vjGeneHeavy: joinGenes(sequence.heavyChain?.vGene, sequence.heavyChain?.jGene),
          vjGeneLight: joinGenes(sequence.lightChain?.vGene, sequence.lightChain?.jGene),
          // Both CDR3s come from the sequence so the two columns are sourced consistently;
          // overlay.cdr3Sequence is the node-level representative, which can differ.
          cdrh3: sequence.heavyChain?.regions?.cdr3 ?? overlay.cdr3Sequence,
          cdrl3: sequence.lightChain?.regions?.cdr3 ?? '',
          nodeSize: sequence.metadata.barcodes.length,
          aps: sequence.metadata.aps ?? null,
          liabilityScore: sequence.metadata.liabilityScore ?? null,
          shms: sequence.metadata.shms ?? null,
          sequenceData: sequence,
          parent: clonotype,
        });
      }
    }
    return rows;
  }, [clonotypes, uniqueNodes]);

  return {
    exports,
    clonotypes,
    clonotypeDefinition,
    rowsByNodeId,
    isLoading: exportsLoading || antibodyLoading,
    error: exportsError ?? antibodyError,
  };
}
